(() => {
	// Prevent multiple loader executions
	if (window.__kalioWidgetLoaded) {
		if (import.meta.env.DEV) {
			console.warn("[Kalio] Widget loader already executed");
		}
		return;
	}
	const pageConfig = window.KalioConfig || {};
	const isDev =
		import.meta.env?.NODE_ENV === "development" ||
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1";

	// Resolve API URL: page override > preview detection > env var > localhost
	function getPreviewApiUrl(): string | null {
		if (import.meta.env.VITE_VERCEL_ENV === "preview") {
			const prNumber = import.meta.env.VITE_PR_NUMBER;
			if (prNumber) {
				return `https://pr-${prNumber}.api-dev-preview.kalio.ai`;
			}
		}
		return null;
	}

	const apiUrl =
		pageConfig.apiUrl || getPreviewApiUrl() || import.meta.env.VITE_KALIO_API_URL || "http://localhost:8000";
	const cdnBaseUrl = import.meta.env.VITE_KALIO_CDN_BASE_URL || "https://cdn.kalio.ai";

	/**
	 * Get HubSpot UTK cookie for visitor identification.
	 * Must be called BEFORE Shadow DOM creation as cookies aren't accessible from within.
	 */
	function getHubSpotUTK(): string | null {
		const match = document.cookie.match(/hubspotutk=([^;]+)/);
		return match?.[1] ?? null;
	}

	/**
	 * Initialize PostHog analytics before widget mounts
	 * This ensures posthog.get_distinct_id() is available immediately
	 */
	async function initPostHog(): Promise<void> {
		// Skip if already initialized
		if (window.kalioSense) return;

		const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
		if (!posthogKey) {
			if (import.meta.env.DEV) {
				console.debug("[Kalio] PostHog key not configured, skipping analytics");
			}
			return;
		}

		try {
			const { default: posthog } = await import("posthog-js");

			// Wait for PostHog to fully initialize before resolving
			// This ensures posthog.get_distinct_id() is available when the widget mounts
			await new Promise<void>((resolve) => {
				window.kalioSense = posthog.init(posthogKey, {
					api_host: "https://kalio.ai/relay-Lk3g",
					ui_host: "https://us.posthog.com",
					persistence_name: "klo_sense",
					advanced_disable_decide: true,
					disable_external_dependency_loading: true,
					autocapture: true,
					capture_pageview: true,
					capture_pageleave: true,
					enable_heatmaps: false,
					loaded: (ph) => {
						if (import.meta.env.DEV) {
							console.log("[KalioSense] Analytics initialized in loader");
						}
						ph.register_once({
							widget_source: "embed_shadow_dom",
							widget_version: "1.0.0",
						});

						// Install global error handlers for error capture
						import("./error-capture")
							.then(({ installErrorHandlers }) => {
								installErrorHandlers();
							})
							.catch(() => {
								// Silently fail - error capture is non-critical
							});

						resolve(); // Resolve only after loaded callback fires
					},
					person_profiles: "always",
				});
			});
		} catch (err) {
			if (import.meta.env.DEV) {
				console.error("[Kalio] Failed to load PostHog:", err);
			}
		}
	}

	/**
	 * Fetch agent config from API before mounting widget.
	 * Returns { data, error } so callers can access failure metadata for observability.
	 */
	async function fetchAgentConfig(
		agentId: string,
	): Promise<{ data: Record<string, unknown> | null; error: { status: number; message: string } | null }> {
		try {
			const response = await fetch(`${apiUrl}/api/v1/agents/${agentId}/config`);
			if (!response.ok) {
				if (import.meta.env.DEV) {
					console.warn(`[Kalio] Failed to fetch agent config: ${response.status}`);
				}
				return { data: null, error: { status: response.status, message: response.statusText } };
			}
			return { data: await response.json(), error: null };
		} catch (e) {
			if (import.meta.env.DEV) {
				console.warn("[Kalio] Failed to fetch agent config:", e);
			}
			return { data: null, error: { status: 0, message: e instanceof Error ? e.message : "Network error" } };
		}
	}

	/**
	 * Create Shadow DOM and mount widget
	 */
	function mountWidget(mergedConfig: Record<string, unknown>) {
		// Create widget container only if it doesn't exist
		let host = document.getElementById("kalio-widget-host");
		if (host) {
			if (import.meta.env.DEV) {
				console.warn("[Kalio] Widget host element already exists");
			}
			return;
		}

		host = document.createElement("div");
		host.id = "kalio-widget-host";
		document.body.appendChild(host);

		const shadow = host.attachShadow({ mode: "open" });

		const container = document.createElement("div");
		container.id = "kalio-widget-root";
		container.style.cssText = `
			position: fixed;
			bottom: 20px;
			right: 20px;
			z-index: 2147483647;
		`;

		shadow.appendChild(container);

		// Load widget script
		const widgetScript = document.createElement("script");
		const fileName = isDev ? `/src/lib/index.ts` : `/widget.js`;
		widgetScript.src = cdnBaseUrl + fileName;

		if (isDev) {
			widgetScript.type = "module";
		}

		widgetScript.onload = () => {
			window.KalioWidget?.mount?.(container, mergedConfig, shadow);
		};

		widgetScript.onerror = () => {
			console.error("[Kalio] Failed to load widget script:", widgetScript.src);
		};

		document.head.appendChild(widgetScript);
	}

	/**
	 * Initialize widget with API config fetched first
	 */
	async function init() {
		// Start PostHog init in parallel with API fetch
		const posthogPromise = initPostHog();

		let apiConfig = null;

		// Fetch API config if agentId provided — abort if fetch fails
		if (pageConfig.agentId) {
			const { data, error } = await fetchAgentConfig(pageConfig.agentId);
			if (error) {
				// Ensure PostHog is ready so we can capture the failure event
				await posthogPromise;
				window.kalioSense?.capture("widget_config_load_failed", {
					agent_id: pageConfig.agentId,
					error_status: error.status,
					error_message: error.message,
					api_url: apiUrl,
					page_url: window.location.href,
					referrer: document.referrer,
				});
				if (import.meta.env.DEV) {
					console.warn("[Kalio] Agent config unavailable — widget will not render");
				}
				return;
			}
			apiConfig = data;
		}

		// Set loaded flag only after config is confirmed available
		window.__kalioWidgetLoaded = true;

		// Ensure PostHog is ready before mounting widget
		await posthogPromise;

		// Capture HubSpot UTK cookie if enabled (before Shadow DOM blocks access)
		const hubspotUtk = apiConfig?.enable_hubspot_tracking ? getHubSpotUTK() : null;

		// Merge configs: API provides base theme, page config can override
		const mergedConfig = {
			...pageConfig,
			apiUrl, // Resolved API URL (pageConfig.apiUrl > env > localhost)
			apiConfig, // Pass full API response for use-chat hook
			hubspotUtk, // People Intelligence: HubSpot cookie for visitor identification
			customization: {
				...(apiConfig?.widget_config || {}), // API theme = base
				...(pageConfig.customization || {}), // Page theme = override
			},
		};

		mountWidget(mergedConfig);
	}

	init();
})();
